import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Loader2, MapPin, Save, Shield, X, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LocationAccessPage() {
  const { employee } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editAccess, setEditAccess] = useState([]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [locRes, adminRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/location-access/locations`, { headers: getAuthHeader() }),
        fetch(`${BACKEND_URL}/api/location-access/admins`, { headers: getAuthHeader() })
      ]);
      if (locRes.ok) setLocations(await locRes.json());
      if (adminRes.ok) setAdmins(await adminRes.json());
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const startEditing = (admin) => {
    setEditingId(admin.employee_id);
    setEditAccess(admin.location_access || []);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditAccess([]);
  };

  const toggleLocation = (loc) => {
    setEditAccess(prev =>
      prev.includes(loc) ? prev.filter(l => l !== loc) : [...prev, loc]
    );
  };

  const saveAccess = async (employeeId) => {
    try {
      setSaving(employeeId);
      const res = await fetch(`${BACKEND_URL}/api/location-access/${employeeId}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify({ location_access: editAccess })
      });
      if (!res.ok) throw new Error('Failed to update');
      toast.success('Location access updated');
      setEditingId(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to save location access');
    } finally {
      setSaving(null);
    }
  };

  if (employee?.role !== 'Super Admin') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Access restricted to Super Admin only.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="location-access-page">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Location Access Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Assign location-based access to Admin Head users. They will only see tickets, analytics, and expenses for their assigned locations.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : admins.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No Admin Head users found. Assign the "Admin Head" role to employees first.</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Admin Head Users</CardTitle>
            </CardHeader>
            <CardContent>
              <Table data-testid="location-access-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Name</TableHead>
                    <TableHead className="w-[220px]">Email</TableHead>
                    <TableHead className="w-[100px]">Base Location</TableHead>
                    <TableHead>Assigned Locations</TableHead>
                    <TableHead className="w-[120px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map((admin) => (
                    <TableRow key={admin.employee_id} data-testid={`admin-row-${admin.employee_id}`}>
                      <TableCell className="font-medium">{admin.full_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{admin.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="gap-1">
                          <MapPin className="w-3 h-3" />
                          {admin.location || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {editingId === admin.employee_id ? (
                          <ScrollArea className="max-h-[160px]">
                            <div className="flex flex-wrap gap-1.5">
                              {locations.map((loc) => {
                                const isSelected = editAccess.includes(loc);
                                return (
                                  <button
                                    key={loc}
                                    onClick={() => toggleLocation(loc)}
                                    data-testid={`loc-toggle-${loc}`}
                                    className={`
                                      inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer border
                                      ${isSelected
                                        ? 'bg-primary text-primary-foreground border-primary'
                                        : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'
                                      }
                                    `}
                                  >
                                    {isSelected && <Check className="w-3 h-3" />}
                                    {loc}
                                  </button>
                                );
                              })}
                            </div>
                            {editAccess.length === 0 && (
                              <p className="text-xs text-amber-600 mt-2">
                                No locations selected = unrestricted access (sees all data)
                              </p>
                            )}
                          </ScrollArea>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {(admin.location_access && admin.location_access.length > 0) ? (
                              admin.location_access.map((loc) => (
                                <Badge key={loc} variant="secondary" className="text-xs">
                                  {loc}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-xs text-amber-600 font-medium">
                                Unrestricted (all locations)
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {editingId === admin.employee_id ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={cancelEditing}
                              data-testid="cancel-edit-btn"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => saveAccess(admin.employee_id)}
                              disabled={saving === admin.employee_id}
                              data-testid="save-access-btn"
                            >
                              {saving === admin.employee_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Save className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startEditing(admin)}
                            data-testid={`edit-access-${admin.employee_id}`}
                          >
                            Edit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="bg-muted/30">
          <CardContent className="py-4">
            <div className="flex gap-3">
              <Shield className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground space-y-1">
                <p><strong>How it works:</strong></p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Admin Head users with assigned locations will only see tickets, dashboard stats, analytics, and expenses for those locations.</li>
                  <li>If no locations are assigned, the Admin Head has unrestricted access to all data.</li>
                  <li>Super Admin always has full access regardless of location settings.</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
